import type { AdminStore, AdminStoreListParams, AdminStoreSummary } from '@/client/types/stores';

/**
 * The roster's one-click segments. Each maps to a server flag filter and a
 * count from `/summary`, so a tile is both the headline number and the filter.
 */
export interface StoreSegment {
  key: string;
  label: string;
  hint: string;
  params: Partial<AdminStoreListParams>;
  count: (s: AdminStoreSummary) => number;
  /** Tailwind text colour for a non-zero count. */
  tone: string;
}

export const STORE_SEGMENTS: StoreSegment[] = [
  { key: 'open', label: 'Open', hint: 'Seller has the store open', params: { active: 1 }, count: (s) => s.open, tone: 'text-green-700' },
  { key: 'closed', label: 'Closed', hint: 'Closed by the seller or by staff', params: { active: 0 }, count: (s) => s.closed, tone: 'text-slate-700' },
  { key: 'review', label: 'In review', hint: 'Held for staff review', params: { review: 1 }, count: (s) => s.review, tone: 'text-amber-700' },
  { key: 'unpublished', label: 'Unpublished', hint: 'Not visible on the storefront', params: { published: 0 }, count: (s) => s.total - s.published, tone: 'text-gray-700' },
  { key: 'suspended', label: 'Suspended', hint: 'Listings dark, payouts frozen', params: { suspended: 1 }, count: (s) => s.suspended, tone: 'text-red-700' },
  { key: 'force_closed', label: 'Force-closed', hint: 'Closed by staff; seller cannot re-open', params: { admin_closed: 1 }, count: (s) => s.admin_closed, tone: 'text-red-700' },
  { key: 'banned', label: 'Banned', hint: 'Permanently barred', params: { banned: 1 }, count: (s) => s.banned, tone: 'text-red-800' },
  { key: 'payout_held', label: 'Payouts held', hint: 'Staff hold on payouts', params: { payout_held: 1 }, count: (s) => s.payout_held, tone: 'text-orange-700' },
  { key: 'verified', label: 'Verified', hint: 'Verified badge', params: { verified: 1 }, count: (s) => s.verified, tone: 'text-blue-700' },
  { key: 'featured', label: 'Featured', hint: 'Featured placement', params: { featured: 1 }, count: (s) => s.featured, tone: 'text-purple-700' },
  { key: 'plan_exempt', label: 'Plan exempt', hint: 'Exempt from plan billing', params: { plan_exempt: 1 }, count: (s) => s.plan_exempt, tone: 'text-teal-700' },
];

const BY_KEY = new Map(STORE_SEGMENTS.map((s) => [s.key, s]));

export const parseSegments = (raw: string | null): string[] =>
  (raw ?? '').split(',').filter((k) => BY_KEY.has(k));

/** Toggle a segment; one that filters the same flag (Open / Closed) replaces it. */
export const toggleSegment = (active: string[], key: string): string[] => {
  const flags = Object.keys(BY_KEY.get(key)?.params ?? {});
  const rest = active.filter(
    (k) => k !== key && !Object.keys(BY_KEY.get(k)!.params).some((f) => flags.includes(f)),
  );
  return active.includes(key) ? rest : [...rest, key];
};

export const segmentParams = (active: string[]): Partial<AdminStoreListParams> =>
  Object.assign({}, ...active.map((k) => BY_KEY.get(k)?.params ?? {}));

// ── Row formatting shared by the card and list views ───────────────────────

export const ownerLabel = (s: AdminStore): string =>
  s.owner?.name ?? (s.oid != null ? `Org #${s.oid}` : `User #${s.uid ?? '—'}`);

export const formatGmv = (gmv: unknown): string =>
  typeof gmv === 'number'
    ? new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS', notation: 'compact', maximumFractionDigits: 1 }).format(gmv)
    : '—';

export const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Plan in a few words: tier, exemption, or none — plus expiry state. */
export const planSummary = (s: AdminStore): { label: string; detail?: string; expired?: boolean } => {
  if (s.plan_exempt) return { label: 'Exempt', detail: s.plan_tier ?? undefined };
  if (s.plid == null && !s.plan_tier) return { label: 'No plan' };
  const expires = s.date_plan_expires ? new Date(s.date_plan_expires) : null;
  const expired = !!expires && expires.getTime() < Date.now();
  return {
    label: s.plan_tier ?? `Plan #${s.plid}`,
    detail: expires ? `${expired ? 'Expired' : 'Renews'} ${formatDate(s.date_plan_expires)}` : undefined,
    expired,
  };
};

/** Why the store is in its current state, when staff recorded a reason. */
export const statusReason = (s: AdminStore): string | undefined =>
  (s.banned && s.ban_reason) || (s.suspended && s.suspend_reason) || (s.review && s.review_reason)
  || (s.admin_closed && s.admin_close_reason) || undefined;

/** The slug column already carries its `/store/…` path. */
export const storefrontPath = (s: AdminStore): string | undefined =>
  s.slug ? `/${s.slug.replace(/^\/+/, '')}` : undefined;
